import { ChangeDetectionStrategy, Component, computed, effect, Injector, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { debounceTime, map, startWith, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import isNil from 'lodash/isNil';
import { PageTitleService } from '@iris/common/services/page-name/page-title.service';
import { MAT_MENU_DEFAULT_OPTIONS } from '@angular/material/menu';
import { StrakitSelectComponent } from '@ngx-strakit/cdk/controls';

@Component({
  selector: 'iris-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss', './dynamic-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: {
        overlayPanelClass: 'header-menu-container',
        xPosition: 'before',
        overlapTrigger: false,
      },
    },
  ],
  standalone: false,
})
export class IrisHeaderComponent implements OnInit, OnDestroy {
  readonly pageTitleService = inject(PageTitleService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly translateService = inject(TranslateService);
  private readonly injectorService = inject(Injector);

  @ViewChild('projectSelector', { static: false }) projectSelector!: StrakitSelectComponent;

  projectControl = new FormControl();
  searchIsOpened = false;

  projectSelectorPlaceholder = `${this.translateService.instant('label.TypeToSearch')}...`;

  private projectChangeSubscription: Subscription | null = null;
  private readonly unsubscribe$ = new Subject<void>();

  readonly isApplcationSelected = computed(() => {
    return !!this.globalSandbox.currentApplication();
  });

  readonly globalSearchEnabled = computed(() => {
    return !!this.globalSandbox.currentApplication()?.globalSearchEnabled;
  });
  readonly projectSelectorEnabled = this.globalSandbox.projectSelectorEnabled;
  readonly displayProjectSelector = computed(() => {
    return this.isApplcationSelected() && this.projectSelectorEnabled();
  });
  readonly displayGlobalSearch = computed(() => {
    return this.isApplcationSelected() && this.globalSearchEnabled();
  });

  constructor() {
    this.globalSandbox.currentProjectId$.pipe(
      tap(projectId => {
        if (isNil(projectId)) {
          this.projectSelectorPlaceholder = this.translateService.instant('label.AllProjects');
        }
        this.projectControl.setValue(projectId, { emitEvent: false });
      }),
      takeUntil(this.unsubscribe$),
    ).subscribe();
  }

  ngOnInit(): void {
    effect(
      () => {
        if (this.projectSelectorEnabled()) {
          setTimeout(() => {
            if (!this.projectChangeSubscription) {
              this.projectChangeSubscription = combineLatest([
                this.projectControl.valueChanges,
                this.projectSelector ? this.projectSelector?.blurEvent.pipe(startWith(null)) : of(null as Event),
              ]).pipe(
                map(([projectId, _]) => projectId),
                withLatestFrom(this.globalSandbox.currentProjectId$),
                debounceTime(500),
                tap(([projectId, prevId]) => {
                  const isSelectorOpen = this.projectSelector?.isOpen;
                  if (prevId === projectId || (!projectId && isSelectorOpen)) {
                    return;
                  }

                  this.globalSandbox.changeCurrentProjectId(projectId, true);
                }),
                takeUntil(this.unsubscribe$),
              ).subscribe();
            }
          });
        } else {
          if (this.projectChangeSubscription) {
            this.projectChangeSubscription.unsubscribe();
            this.projectChangeSubscription = null;
          }
        }
      },
      { injector: this.injectorService },
    );
  }

  toggleSearchExpansion(isOpen: boolean): void {
    this.searchIsOpened = isOpen;
  }

  ngOnDestroy(): void {
    this.projectChangeSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
